(function () {
  const config = window.MRJCurrencyConfig || {};
  const selectors = [
    '.yv-product-price', '.yv-product-compare-price', '.yv-product-compare-value', '.yv-compare-price',
    '.yv-spotlight-product-price', '[data-cart-total-price]',
    '[data-cart-original-price]', '[data-unit-price]', '[data-mrj-base-minor]'
  ].join(',');
  const cacheKey = 'MRJCurrencySnapshot';
  const fetchTimeoutMs = 3000;
  let snapshot;
  let processing = false;
  let observerStarted = false;

  function revealPrices() {
    if (window.MRJCurrencyRevealTimer) {
      window.clearTimeout(window.MRJCurrencyRevealTimer);
      window.MRJCurrencyRevealTimer = null;
    }
    document.documentElement.classList.remove('mrj-currency-pending');
    document.documentElement.classList.add('mrj-currency-ready');
  }

  function isValidSnapshot(value) {
    if (!value || typeof value !== 'object') return false;
    if (config.snapshotId && value.id !== config.snapshotId) return false;
    if (!value.rates || typeof value.rates !== 'object') return false;
    return true;
  }

  function cachedSnapshot() {
    if (!config.snapshotId) return null;
    try {
      const value = JSON.parse(window.localStorage.getItem(cacheKey));
      return isValidSnapshot(value) ? value : null;
    } catch (error) {
      return null;
    }
  }

  function cacheSnapshot(value) {
    if (!config.snapshotId) return;
    try {
      window.localStorage.setItem(cacheKey, JSON.stringify(value));
    } catch (error) {
      // Currency conversion must continue when storage is unavailable.
    }
  }

  function effectiveSnapshot(value) {
    if (value.source === 'fixer' && value.publishedAt && Date.now() - new Date(value.publishedAt).getTime() > 72 * 60 * 60 * 1000) {
      return Object.assign({}, value, { rates: { INR: '1' }, countryCurrencies: {} });
    }
    return value;
  }

  function contextFor(value) {
    const countryCurrencies = value.countryCurrencies || value.country_currencies || {};
    const rates = value.rates || {};
    const currencyCode = countryCurrencies[String(config.countryCode || '').toUpperCase()] || 'INR';
    const rate = currencyCode === String(value.baseCurrency || 'INR').toUpperCase()
      ? 1
      : Number(rates[currencyCode]);
    if (!Number.isFinite(rate) || rate <= 0) return { currencyCode: 'INR', rate: 1 };
    return { currencyCode, rate, currency: (value.currencies || {})[currencyCode] };
  }

  function format(amount, currencyCode, currency) {
    const symbol = currency && typeof currency.symbol === 'string' ? currency.symbol.trim() : '';
    const decimalPlaces = currency && Number.isInteger(currency.decimalPlaces)
      ? currency.decimalPlaces
      : -1;
    if (symbol && decimalPlaces >= 0 && decimalPlaces <= 20) {
      const number = new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'decimal',
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces
      }).format(amount);
      return symbol + number;
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
      style: 'currency', currency: currencyCode,
      minimumFractionDigits: currencyCode === 'INR' ? 0 : undefined,
      maximumFractionDigits: currencyCode === 'INR' ? 0 : undefined
    }).format(amount);
  }

  function convertElement(element, context) {
    const baseMinor = Number(element.dataset.mrjBaseMinor);
    if (!Number.isInteger(baseMinor) || baseMinor < 0) return;
    const base = baseMinor / 100;
    const rendered = format(base * context.rate, context.currencyCode, context.currency);
    if (element.textContent !== rendered) element.textContent = rendered;
    element.dataset.mrjRendered = rendered;
    element.dataset.mrjCurrency = context.currencyCode;
  }

  function apply(root) {
    if (!snapshot || processing) return;
    processing = true;
    try {
      const context = contextFor(snapshot);
      const elements = [];
      if (root instanceof Element && root.matches(selectors)) elements.push(root);
      if (root.querySelectorAll) elements.push(...root.querySelectorAll(selectors));
      elements.forEach((element) => convertElement(element, context));
      window.MRJCurrency = {
        snapshotId: snapshot.id,
        countryCode: String(config.countryCode || 'IN').toUpperCase(),
        currencyCode: context.currencyCode,
        rate: context.rate
      };
      document.documentElement.dataset.mrjCurrency = context.currencyCode;
    } finally {
      processing = false;
    }
  }

  function observePrices() {
    if (observerStarted) return;
    observerStarted = true;
    new MutationObserver((records) => {
      if (processing) return;
      records.forEach((record) => {
        if (record.type === 'attributes') {
          apply(record.target);
        }
        record.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) apply(node);
        });
      });
    }).observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-mrj-base-minor']
    });
  }

  async function load() {
    const url = config.snapshotUrl || config.fallbackUrl;
    try {
      const cached = cachedSnapshot();
      if (cached) {
        window.MRJCurrencySnapshot = cached;
        snapshot = effectiveSnapshot(cached);
        apply(document);
        observePrices();
        return;
      }
      if (!url) throw new Error('Snapshot URL is unavailable');

      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), fetchTimeoutMs);
      let response;
      try {
        response = await fetch(url, { cache: 'no-cache', signal: controller.signal });
      } finally {
        window.clearTimeout(timeout);
      }
      if (!response.ok) throw new Error('HTTP ' + response.status);
      const downloadedSnapshot = await response.json();
      if (!isValidSnapshot(downloadedSnapshot)) throw new Error('Snapshot validation failed');
      window.MRJCurrencySnapshot = downloadedSnapshot;
      cacheSnapshot(downloadedSnapshot);
      snapshot = effectiveSnapshot(downloadedSnapshot);
      apply(document);
      observePrices();
    } catch (error) {
      console.warn('[MRJ Currency] Unable to load snapshot; retaining INR prices.', error);
    } finally {
      revealPrices();
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', load);
  else load();
})();
